/**
 * useAssignableTechnicians
 * 
 * Canonical hook for fetching technicians that can be assigned to visits.
 * This hook should be used by ALL scheduling modals to ensure consistent filtering.
 * 
 * Backend endpoint applies these filters:
 * 1. technicians.tenant_id = current company
 * 2. technicians.is_active = true
 * 3. Owner technician included ONLY if allow_owner_as_technician = true
 */

import { useState, useEffect, useCallback } from "react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export function useAssignableTechnicians() {
  const [technicians, setTechnicians] = useState([]);
  const [allTechnicians, setAllTechnicians] = useState([]); // Includes inactive for historical display
  const [allowOwnerAsTechnician, setAllowOwnerAsTechnician] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchTechnicians = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Fetch assignable technicians (filtered by backend)
      const [assignableRes, allRes] = await Promise.all([
        fetch(`${API}/technicians/assignable`, { credentials: "include" }),
        fetch(`${API}/technicians`, { credentials: "include" }), // For historical display
      ]);
      
      if (assignableRes.ok) {
        const data = await assignableRes.json();
        setTechnicians(data.technicians || []);
        setAllowOwnerAsTechnician(data.allow_owner_as_technician ?? true);
      } else {
        throw new Error("Failed to fetch assignable technicians");
      }
      
      if (allRes.ok) {
        const allData = await allRes.json();
        setAllTechnicians(allData.technicians || []);
      }
    } catch (err) {
      console.error("Error fetching technicians:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTechnicians();
  }, [fetchTechnicians]);

  // Helper to get technician by ID (for displaying historical assignments)
  const getTechnicianById = useCallback((techId) => {
    return allTechnicians.find(t => t.technician_id === techId);
  }, [allTechnicians]);

  // Helper to check if a technician can be assigned (is in the assignable list)
  const canAssignTechnician = useCallback((techId) => {
    return technicians.some(t => t.technician_id === techId);
  }, [technicians]);

  // Helper to get default technician (owner first, then first available)
  // BULLETPROOF: Auto-select when only one option or prefer owner
  const getDefaultTechnician = useCallback(() => {
    if (technicians.length === 0) return null;
    if (technicians.length === 1) return technicians[0];
    
    // Prefer owner if available
    const owner = technicians.find(t => t.is_owner);
    if (owner) return owner;
    
    // Otherwise return first active
    return technicians[0];
  }, [technicians]);

  return {
    // Assignable technicians (filtered by active + owner setting)
    technicians,
    // All technicians including inactive (for historical display)
    allTechnicians,
    // Whether owner can be assigned
    allowOwnerAsTechnician,
    // Loading and error states
    loading,
    error,
    // Refetch function
    refetch: fetchTechnicians,
    // Helper functions
    getTechnicianById,
    canAssignTechnician,
    getDefaultTechnician,
  };
}

export default useAssignableTechnicians;
